import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Power, Wallet, TrendingUp, MessageCircle, RefreshCcw, TriangleAlert, Sparkles } from 'lucide-react';

interface Config {
  id: string;
  is_enabled: boolean;
  monthly_budget_brl: number;
  current_month_spend_brl: number;
  current_month_ref: string;
  paused_reason: string | null;
  auto_return_hours: number;
  model_text: string;
  paused_since?: string | null;
  paused_error_detail?: string | null;
}

interface Stats {
  activeConversations: number;
  agentMessages: number;
  lorenaMessages: number;
  drafts: number;
}

function formatSinceRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const minutes = Math.max(1, Math.round(diffMs / 60000));
  if (minutes < 60) return `ha ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `ha ${hours}h`;
  const days = Math.round(hours / 24);
  return `ha ${days}d`;
}

interface BrainStatus {
  openai_configured: boolean;
  model_text: string;
  model_audio: string;
}

export default function OverviewTab({ sellerId, sellerName }: { sellerId: string; sellerName: string }) {
  const [config, setConfig] = useState<Config | null>(null);
  const [stats, setStats] = useState<Stats>({ activeConversations: 0, agentMessages: 0, lorenaMessages: 0, drafts: 0 });
  const [busy, setBusy] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [autoReturnInput, setAutoReturnInput] = useState('');
  const [brainStatus, setBrainStatus] = useState<BrainStatus | null>(null);

  useEffect(() => {
    void load();
    void checkBrainStatus();
  }, [sellerId]);

  async function checkBrainStatus() {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        (import.meta.env.VITE_SUPABASE_URL as string) + '/functions/v1/ai-agent-brain',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ action: 'status' }),
        }
      );
      if (resp.ok) {
        const json = await resp.json();
        setBrainStatus(json as BrainStatus);
      }
    } catch (_e) {
      setBrainStatus(null);
    }
  }

  async function load() {
    setBusy(true);
    const { data: cfg } = await supabase
      .from('ai_agent_config')
      .select('*')
      .eq('seller_id', sellerId)
      .maybeSingle();
    if (cfg) {
      setConfig(cfg as Config);
      setBudgetInput(String(cfg.monthly_budget_brl));
      setAutoReturnInput(String(cfg.auto_return_hours));
    } else {
      setConfig(null);
      setBudgetInput('500');
      setAutoReturnInput('24');
    }

    const { data: conv } = await supabase
      .from('ai_conversations')
      .select('id')
      .eq('seller_id', sellerId)
      .is('ended_at', null);
    const convIds = (conv ?? []).map((c: any) => c.id);

    let agent = 0;
    let lorena = 0;
    if (convIds.length > 0) {
      const { count: a } = await supabase
        .from('ai_messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', convIds)
        .eq('sender', 'agent');
      const { count: l } = await supabase
        .from('ai_messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', convIds)
        .eq('sender', 'lorena');
      agent = a ?? 0;
      lorena = l ?? 0;
    }

    const { count: drafts } = await supabase
      .from('ai_drafts')
      .select('id', { count: 'exact', head: true })
      .eq('seller_id', sellerId)
      .eq('status', 'pending');

    setStats({
      activeConversations: convIds.length,
      agentMessages: agent,
      lorenaMessages: lorena,
      drafts: drafts ?? 0,
    });
    setBusy(false);
  }

  async function toggleEnabled() {
    if (!config) return;
    const nextEnabled = !config.is_enabled;
    const { error } = await supabase
      .from('ai_agent_config')
      .update({ is_enabled: nextEnabled, paused_reason: nextEnabled ? null : config.paused_reason ?? 'paused_by_manager' })
      .eq('id', config.id);
    if (error) {
      alert('Nao foi possivel atualizar: ' + error.message);
      return;
    }
    void load();
  }

  async function createConfig() {
    const { data: seller } = await supabase.from('users').select('unit_id').eq('id', sellerId).maybeSingle();
    const { error } = await supabase.from('ai_agent_config').insert({
      seller_id: sellerId,
      unit_id: (seller as any)?.unit_id ?? null,
      is_enabled: false,
      monthly_budget_brl: 500,
    });
    if (error) {
      alert('Nao foi possivel criar: ' + error.message);
      return;
    }
    void load();
  }

  async function saveSettings() {
    if (!config) return;
    const budget = Number(budgetInput);
    const autoReturn = Number(autoReturnInput);
    if (Number.isNaN(budget) || budget < 0) return alert('Orcamento invalido');
    if (Number.isNaN(autoReturn) || autoReturn < 1) return alert('Retorno automatico invalido');
    const { error } = await supabase
      .from('ai_agent_config')
      .update({ monthly_budget_brl: budget, auto_return_hours: autoReturn })
      .eq('id', config.id);
    if (error) return alert('Erro: ' + error.message);
    void load();
  }

  if (busy && !config) return <div className="text-sm text-slate-500">Carregando...</div>;

  if (!config) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <Power className="mx-auto mb-3 h-10 w-10 text-slate-400" />
        <p className="mb-4 text-sm text-slate-600">
          O agente ainda nao foi configurado para <strong>{sellerName}</strong>.
        </p>
        <button
          onClick={createConfig}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          Ativar configuracao inicial
        </button>
      </div>
    );
  }

  const spendPct = config.monthly_budget_brl > 0
    ? Math.min(100, (config.current_month_spend_brl / config.monthly_budget_brl) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {config.paused_reason === "openai_quota_exhausted" && (
        <div className="flex items-start gap-3 rounded-xl border-2 border-red-400 bg-red-50 p-4 shadow-sm">
          <TriangleAlert className="mt-0.5 h-6 w-6 flex-shrink-0 text-red-600" />
          <div className="flex-1 text-sm text-red-900">
            <div className="text-base font-bold">IA pausada — credito da OpenAI acabou</div>
            <div className="mt-1 text-sm">
              A conta da OpenAI ficou sem saldo e a IA parou de responder as mensagens.
              {config.paused_since && (
                <> Isso aconteceu {formatSinceRelative(config.paused_since)}.</>
              )}
              {" "}A reativacao e <strong>automatica</strong>: assim que voce recarregar o credito, a IA volta sozinha na proxima mensagem que chegar.
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href="https://platform.openai.com/settings/organization/billing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              >
                Abrir cobranca da OpenAI
              </a>
              <span className="text-xs text-red-800">Voce nao precisa clicar em nada aqui — a IA volta sozinha apos o credito.</span>
            </div>
            {config.paused_error_detail && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs font-semibold text-red-800">Ver detalhe tecnico</summary>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-red-100 p-2 text-[10px] text-red-900">{config.paused_error_detail}</pre>
              </details>
            )}
          </div>
        </div>
      )}
      {brainStatus && !brainStatus.openai_configured && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <TriangleAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Chave da OpenAI ainda nao esta ativa no servidor.</div>
            <div className="mt-1 text-xs">
              O agente vai continuar registrando conversas, mas nao consegue responder ate a chave ser publicada
              no ambiente. Isso pode levar alguns minutos apos a chave ser adicionada.
            </div>
          </div>
        </div>
      )}

      {brainStatus?.openai_configured && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <div className="font-semibold">Agente conectado a OpenAI.</div>
            <div className="mt-1 text-xs">
              Modelo de texto: <strong>{brainStatus.model_text}</strong> — modelo de audio:{' '}
              <strong>{brainStatus.model_audio}</strong>.
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className={'rounded-full p-3 ' + (config.is_enabled ? 'bg-emerald-100' : 'bg-slate-100')}>
            <Power className={'h-6 w-6 ' + (config.is_enabled ? 'text-emerald-600' : 'text-slate-400')} />
          </div>
          <div>
            <div className="text-sm text-slate-500">Status do agente</div>
            <div className="text-lg font-semibold text-slate-900">
              {config.is_enabled ? 'Ativo — atendendo com a Lorena' : 'Pausado'}
            </div>
            {config.paused_reason && !config.is_enabled && (
              <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                <TriangleAlert className="h-3.5 w-3.5" />
                Motivo: {config.paused_reason}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={toggleEnabled}
          className={
            'rounded-lg px-4 py-2 text-sm font-medium transition ' +
            (config.is_enabled
              ? 'bg-slate-900 text-white hover:bg-slate-800'
              : 'bg-emerald-600 text-white hover:bg-emerald-700')
          }
        >
          {config.is_enabled ? 'Pausar agente' : 'Ativar agente'}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={MessageCircle} label="Conversas ativas" value={stats.activeConversations} tone="emerald" />
        <StatCard icon={TrendingUp} label="Mensagens do agente" value={stats.agentMessages} tone="blue" />
        <StatCard icon={TrendingUp} label="Mensagens da Lorena" value={stats.lorenaMessages} tone="amber" />
        <StatCard icon={Wallet} label="Rascunhos pendentes" value={stats.drafts} tone="slate" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">Orcamento do mes ({config.current_month_ref})</div>
            <div className="text-lg font-semibold text-slate-900">
              R$ {config.current_month_spend_brl.toFixed(2)} / R$ {config.monthly_budget_brl.toFixed(2)}
            </div>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
          </button>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className={
              'h-full transition-all ' +
              (spendPct >= 90 ? 'bg-red-500' : spendPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500')
            }
            style={{ width: spendPct + '%' }}
          />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">Configuracoes</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Orcamento mensal (R$)</label>
            <input
              type="number"
              step="0.01"
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">
              Retornar controle apos (horas)
            </label>
            <input
              type="number"
              value={autoReturnInput}
              onChange={(e) => setAutoReturnInput(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={saveSettings}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  tone: 'emerald' | 'blue' | 'amber' | 'slate';
}) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  } as const;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={'mb-2 inline-flex rounded-lg p-2 ' + tones[tone]}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
